import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Award, MapPin, UserCheck, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";

const Stars = ({ value }) => (
  <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={15} className={i <= Math.round(value || 0) ? "text-[#C9A45C] fill-[#C9A45C]" : "text-gray-700"} />)}</span>
);

export default function PublicApplicatorProfile() {
  const { code } = useParams();
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/applicators/${code}/public`).then(({ data }) => setD(data)).catch(() => setErr(true)); }, [code]);

  if (err) return <div className="min-h-screen bg-[#080808] flex items-center justify-center text-gray-500">Applicator not found.</div>;
  if (!d) return <div className="min-h-screen bg-[#080808] flex items-center justify-center text-gray-500">Loading…</div>;
  const a = d.applicator;
  const groups = d.portfolio.reduce((m, e) => { (m[e.service_name] = m[e.service_name] || []).push(e); return m; }, {});

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="public-applicator-profile">
      <Seo title={`${a.name} · ERRĀH Certified Applicator`} description={`ERRĀH certified applicator ${a.name}`} />
      <div className="max-w-3xl mx-auto px-5 py-14">
        <Link to="/" className="inline-flex items-center gap-2 mb-8"><img src="/errah-logo.png" alt="ERRĀH" className="h-8" /><span className="font-serif tracking-[0.25em] text-sm">ERRĀH</span></Link>

        <div className="hair-border bg-gradient-to-br from-[#141414] to-[#0A0A0A] border-[#C9A45C]/25 p-7">
          <p className="errah-label gold-text flex items-center gap-1.5"><ShieldCheck size={14} /> Certified ERRĀH Applicator</p>
          <div className="flex items-center gap-5 mt-4">
            <div className="w-20 h-20 rounded-full bg-[#C9A45C]/15 border border-[#C9A45C]/40 flex items-center justify-center overflow-hidden shrink-0">
              {a.picture ? <img src={a.picture} alt={a.name} className="w-full h-full object-cover" /> : <UserCheck size={30} className="text-[#C9A45C]" />}
            </div>
            <div>
              <p className="font-display text-2xl">{a.name}</p>
              <p className="text-sm text-gray-400 mt-0.5">{a.applicator_code} · {a.level}</p>
              <div className="flex items-center gap-2 mt-1.5"><Stars value={d.ratings?.overall || a.rating} /><span className="text-sm text-gray-300">{d.ratings?.overall ?? a.rating ?? "—"}</span></div>
              <p className="text-sm text-gray-400 mt-1">{a.jobs_completed || 0} ERRĀH Jobs Completed</p>
            </div>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div><p className="errah-label mb-2 flex items-center gap-1.5"><Award size={13} /> {t("certified_for") || "Certified For"}</p>
              <ul className="space-y-1 text-sm text-gray-300">{d.certified_services.map((s) => <li key={s}>• {s}</li>)}</ul></div>
            <div><p className="errah-label mb-2 flex items-center gap-1.5"><MapPin size={13} /> {t("service_areas")}</p>
              <p className="text-sm text-gray-300">{a.city}</p></div>
          </div>
        </div>

        {d.ratings?.services?.length > 0 && (
          <div className="hair-border bg-white/[0.02] p-5 mt-5">
            <p className="errah-label gold-text mb-3">{t("per_service_ratings")}</p>
            {d.ratings.services.map((r) => <div key={r.service} className="flex items-center justify-between text-sm py-1"><span>{r.service}</span><span className="inline-flex items-center gap-2"><Stars value={r.rating} /> {r.rating}</span></div>)}
          </div>
        )}

        {Object.keys(groups).length > 0 && (
          <div className="mt-5">
            <p className="errah-label gold-text mb-3">{t("portfolio")}</p>
            {Object.entries(groups).map(([svc, entries]) => (
              <div key={svc} className="mb-5">
                <p className="text-sm gold-text mb-2">{svc}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {entries.map((e) => (
                    <div key={e.id} className="hair-border bg-white/[0.02] p-3">
                      <p className="text-xs text-gray-400">{e.vehicle || e.surface} · {e.product} · {e.date}</p>
                      {e.description && <p className="text-xs text-gray-500 mt-1">{e.description}</p>}
                      <div className="flex gap-1.5 mt-2 overflow-x-auto">
                        {[...(e.before || []), ...(e.during || []), ...(e.after || [])].slice(0, 6).map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 object-cover rounded shrink-0" />)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
